import { createFileRoute, Link } from "@tanstack/react-router";
import { PhotoProvider, PhotoView } from "react-photo-view";
import CkEditorContent from "src/components/content/ck-editor-content";
import { CalendarCheck, CalendarX, Navigation2, Tag } from "lucide-react";
import { strapiRepository } from "src/strapi/strapiRepository";
import { referencesBySlugQueryOptions } from "src/react-query/react-query";
import { parseYoutubeId } from "src/lib/parseYoutubeId";
import LiteYouTubeEmbed from "react-lite-youtube-embed";
import { seo } from "src/utils/seo";
import { removeHtmlTags } from "src/lib/removeHtmlTags";
import { ReactNode } from "react";

export const Route = createFileRoute(
  "/_breadcrumbs/_referenceBreadcrumb/reference/$referenceSlug",
)({
  component: RouteComponent,
  // `status` is set by the Strapi admin Preview feature (see strapi/config/admin.ts)
  validateSearch: (search: Record<string, unknown>) => ({
    status:
      search.status === "draft"
        ? ("draft" as const)
        : search.status === "published"
          ? ("published" as const)
          : undefined,
  }),
  loaderDeps: ({ search }) => ({ status: search.status }),
  loader: async ({ context, params, deps }) => {
    const reference = await context.queryClient.ensureQueryData(
      referencesBySlugQueryOptions(params.referenceSlug, deps.status),
    );

    return {
      crumb: reference?.title,
      reference,
    };
  },
  head: ({ loaderData }) => {
    const description = removeHtmlTags(
      (loaderData?.reference?.content ?? "").slice(0, 250),
    );

    return {
      meta: [
        ...seo({
          title: (loaderData?.reference?.title || "Reference") + " | VESAS",
          description,
        }),
      ],
    };
  },
});

const SubheaderBadge = ({ children }: { children: ReactNode }) => {
  return (
    <div
      className={
        "text-foreground/50 flex flex-row items-center gap-2 text-xs md:text-sm"
      }
    >
      {children}
    </div>
  );
};

function RouteComponent() {
  const { reference } = Route.useLoaderData();
  const { getImageUrl } = strapiRepository;

  if (!reference) {
    return <div>Reference nebyla nalezena</div>; //This should never happen
  }

  const youtubeVideoId =
    reference.youtube_link && parseYoutubeId(reference.youtube_link);
  return (
    <article
      className={
        "mx-auto mt-4 max-w-[1200px] px-2 md:my-6 md:px-12 lg:px-16 xl:px-24"
      }
    >
      <div className={"mb-4 border-b md:mb-6"}>
        <h1 className={"text-center text-3xl font-extrabold lg:text-5xl"}>
          {reference.title}
        </h1>
        <div
          className={
            "mt-6 mb-4 flex flex-col items-center justify-between gap-2 md:flex-row md:gap-4"
          }
        >
          <div className={"flex flex-row gap-4"}>
            {reference.location && (
              <SubheaderBadge>
                <Navigation2 size={12} />
                <span>{reference.location}</span>
              </SubheaderBadge>
            )}

            {reference.isFinished ? (
              <SubheaderBadge>
                <CalendarCheck size={12} />
                <span>{reference.year}</span>
              </SubheaderBadge>
            ) : (
              <SubheaderBadge>
                <CalendarX size={12} />
                <span>Právě stavíme</span>
              </SubheaderBadge>
            )}
          </div>
          <div
            className={
              "flex flex-row flex-wrap items-center justify-center gap-2"
            }
          >
            <Tag size={12} className="text-foreground/50" />
            {reference.categories?.map(
              (category) =>
                category.slug && (
                  <Link
                    key={category.id}
                    className={"hover:underline"}
                    to={`/reference/kategorie/$categorySlug`}
                    params={{
                      categorySlug: category.slug,
                    }}
                  >
                    <SubheaderBadge>
                      <span>{category.name}</span>
                    </SubheaderBadge>
                  </Link>
                ),
            )}
          </div>
        </div>
      </div>
      {reference.content && (
        <CkEditorContent htmlContent={reference.content}></CkEditorContent>
      )}

      {(reference.investorCitation || reference.investorImage) && (
        <>
          <div
            className={
              "mt-28 flex flex-col items-center justify-center gap-2 text-center md:flex-row md:gap-0"
            }
          >
            {reference.investorImage && (
              <div className={"flex justify-center"}>
                <img
                  src={getImageUrl(reference.investorImage)}
                  alt={reference.investorImage.alternativeText || "Investor"}
                  className={"h-64 object-cover object-center shadow-lg"}
                />
              </div>
            )}
            <blockquote
              className={
                "bg-foreground relative max-w-96 p-6 text-start leading-7 text-white italic shadow-xl md:-left-6"
              }
            >
              {reference.investorCitation && (
                <div className="mb-2">{reference.investorCitation}</div>
              )}
              {
                <span className={"ml-auto block text-end font-bold"}>
                  {reference.investorName || "Investor"}
                </span>
              }
            </blockquote>
          </div>
        </>
      )}

      {reference.photos && (
        <div>
          <h3 className={"mt-28 mb-8 text-4xl"}>Galerie</h3>
          <PhotoProvider
            overlayRender={(params) => {
              if (!reference.photos?.[params.index].caption) return null;
              return (
                <div
                  className={
                    "bg-foreground/50 text-background absolute bottom-0 z-50 flex h-12 w-full items-center justify-center text-center"
                  }
                >
                  {reference.photos[params.index].caption}
                </div>
              );
            }}
          >
            <div
              className={
                "grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6"
              }
            >
              {reference.photos.map((image) => (
                <PhotoView key={image.id} src={getImageUrl(image)}>
                  <img
                    src={getImageUrl(image)}
                    alt={image.alternativeText}
                    className={
                      "size-40 cursor-pointer object-cover object-center md:size-44"
                    }
                  />
                </PhotoView>
              ))}
            </div>
          </PhotoProvider>
        </div>
      )}
      {reference.youtube_link && (
        <div>
          <h3 className={"mt-20 mb-8 text-4xl"}>Video</h3>
          {youtubeVideoId && (
            <LiteYouTubeEmbed
              id={youtubeVideoId}
              title={reference.title ?? ""}
            />
          )}
        </div>
      )}
    </article>
  );
}
